/* Vault theme — interactions. No dependencies. */
(() => {
  'use strict';

  const P = window.Vault || {};
  const routes = P.routes || {};
  const strings = P.strings || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- helpers ---------- */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || P.moneyFormat || '${{amount}}';
    const delimit = (n, precision, thousands = ',', decimal = '.') => {
      if (isNaN(n) || n == null) return '0';
      const num = (n / 100).toFixed(precision);
      const [whole, frac] = num.split('.');
      return whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands) + (frac ? decimal + frac : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return delimit(cents, 0);
        case 'amount_with_comma_separator': return delimit(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return delimit(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return delimit(cents, 2, "'", '.');
        default: return delimit(cents, 2);
      }
    });
  }

  function parseSection(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return selector ? doc.querySelector(selector) : doc;
  }

  function toast(message, type = 'ok', action) {
    const region = $('[data-toast-region]');
    if (!region) return;
    const el = document.createElement('div');
    el.className = 'toast toast--' + type;
    el.setAttribute('role', type === 'err' ? 'alert' : 'status');
    const text = document.createElement('span');
    text.textContent = message;
    el.appendChild(text);
    if (action) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'toast__action';
      btn.textContent = action.label;
      btn.addEventListener('click', () => { action.onClick(); el.remove(); });
      el.appendChild(btn);
    }
    region.appendChild(el);
    requestAnimationFrame(() => el.classList.add('is-in'));
    setTimeout(() => { el.classList.remove('is-in'); setTimeout(() => el.remove(), 300); }, 3800);
  }

  /* ---------- drawers (mobile nav, cart, filters) ---------- */
  let lastFocus = null;
  const openDrawers = [];

  function openDrawer(id, opts = {}) {
    const d = document.getElementById(id);
    if (!d) return;
    if (!opts.silent) lastFocus = document.activeElement;
    d.classList.add('is-open');
    if (opts.instant) d.classList.add('no-anim');
    d.setAttribute('aria-hidden', 'false');
    if (!openDrawers.includes(id)) openDrawers.push(id);
    document.documentElement.classList.add('is-locked');
    $$(`[aria-controls="${id}"]`).forEach(b => b.setAttribute('aria-expanded', 'true'));
    const panel = $('.drawer__panel', d);
    if (panel && !opts.silent) setTimeout(() => panel.focus({ preventScroll: true }), opts.instant ? 0 : 60);
    if (opts.instant) requestAnimationFrame(() => d.classList.remove('no-anim'));
  }

  function closeDrawer(id) {
    const d = document.getElementById(id);
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    openDrawers.splice(openDrawers.indexOf(id), 1);
    $$(`[aria-controls="${id}"]`).forEach(b => b.setAttribute('aria-expanded', 'false'));
    if (!openDrawers.length && !$('dialog[open]')) document.documentElement.classList.remove('is-locked');
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); openDrawer(opener.dataset.drawerOpen); return; }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) { const d = closer.closest('[data-drawer]'); if (d) closeDrawer(d.id); return; }
    const cartOpen = e.target.closest('[data-cart-open]');
    if (cartOpen && document.getElementById('CartDrawer')) { e.preventDefault(); openDrawer('CartDrawer'); }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && openDrawers.length && !$('dialog[open]')) closeDrawer(openDrawers[openDrawers.length - 1]);
    if (e.key === 'Tab' && openDrawers.length) {
      const d = document.getElementById(openDrawers[openDrawers.length - 1]);
      const f = $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, summary, [tabindex]:not([tabindex="-1"])', $('.drawer__panel', d)).filter(el => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ---------- cart ---------- */
  const Cart = {
    sectionsUrl() { return window.location.pathname; },

    render(sections) {
      if (!sections || !sections['cart-drawer']) return;
      const fresh = parseSection(sections['cart-drawer'], '#CartDrawer');
      const current = document.getElementById('CartDrawer');
      if (!fresh || !current) return;
      const wasOpen = current.classList.contains('is-open');
      current.replaceWith(fresh);
      if (wasOpen) { fresh.classList.add('is-open', 'no-anim'); fresh.setAttribute('aria-hidden', 'false'); requestAnimationFrame(() => fresh.classList.remove('no-anim')); }
      Cart.updateCount(parseInt(fresh.dataset.count || '0', 10));
    },

    updateCount(count) {
      $$('[data-cart-count]').forEach(el => {
        const changed = el.textContent.trim() !== String(count);
        el.textContent = count; el.classList.toggle('is-empty', count === 0);
        if (changed && count > 0) { el.classList.remove('is-bump'); void el.offsetWidth; el.classList.add('is-bump'); }
      });
      $$('.header__cart').forEach(a => a.setAttribute('aria-label', `Cart (${count})`));
    },

    async add(formData) {
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', Cart.sectionsUrl());
      const res = await fetch(routes.cartAdd || '/cart/add', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || strings.addError);
      return data;
    },

    async change(line, quantity) {
      const drawer = document.getElementById('CartDrawer');
      drawer && drawer.classList.add('is-busy');
      try {
        const res = await fetch(routes.cartChange || '/cart/change', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: ['cart-drawer'], sections_url: Cart.sectionsUrl() })
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not update your cart.');
        Cart.render(data.sections);
      } catch (err) {
        const box = $('[data-cart-error]');
        if (box) { box.textContent = err.message; box.hidden = false; } else toast(err.message, 'err');
      } finally {
        const d = document.getElementById('CartDrawer');
        d && d.classList.remove('is-busy');
      }
    }
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-line-qty]');
    if (!btn) return;
    e.preventDefault();
    Cart.change(parseInt(btn.dataset.lineQty, 10), Math.max(0, parseInt(btn.dataset.qty, 10)));
  });

  /* ---------- product form (add to cart) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }
    async onSubmit(e) {
      e.preventDefault();
      const btn = $('[data-add-button]', this.form) || $('[type="submit"]', this.form);
      const label = btn && $('[data-add-label]', btn);
      const errBox = $('[data-form-error]', this);
      if (btn && btn.getAttribute('aria-disabled') === 'true') return;
      const original = label ? label.textContent : '';
      if (btn) { btn.setAttribute('aria-disabled', 'true'); btn.classList.add('is-loading'); }
      if (label) label.textContent = strings.adding || 'Adding…';
      if (errBox) errBox.hidden = true;
      try {
        const data = await Cart.add(new FormData(this.form));
        Cart.render(data.sections);
        const modal = this.closest('dialog');
        if (modal && modal.open) modal.close();
        if (P.settings && P.settings.cartDrawer && document.getElementById('CartDrawer')) {
          openDrawer('CartDrawer');
          toast(strings.added || 'Added to your cart');
        } else {
          toast(strings.added || 'Added to your cart', 'ok', { label: 'View cart', onClick: () => { window.location.href = routes.cart; } });
        }
      } catch (err) {
        if (errBox) { errBox.textContent = err.message; errBox.hidden = false; }
        else toast(err.message, 'err');
      } finally {
        if (btn) { btn.removeAttribute('aria-disabled'); btn.classList.remove('is-loading'); }
        if (label) label.textContent = original;
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- variant picker + quantity ---------- */
  class ProductBuy extends HTMLElement {
    connectedCallback() {
      const json = $('script[data-variants]', this);
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.addEventListener('change', (e) => { if (e.target.matches('[data-option-index]')) this.onOptionChange(); });
      this.addEventListener('click', (e) => {
        const step = e.target.closest('[data-qty-step]');
        if (!step) return;
        const input = $('.qty__input', this);
        const next = Math.min(99, Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(step.dataset.qtyStep, 10)));
        input.value = next;
      });
    }
    selectedOptions() {
      const count = this.variants.length ? this.variants[0].options.length : 0;
      const out = [];
      for (let i = 0; i < count; i++) {
        const radio = $(`input[data-option-index="${i}"]:checked`, this);
        const select = $(`select[data-option-index="${i}"]`, this);
        out.push(radio ? radio.value : select ? select.value : null);
      }
      return out;
    }
    onOptionChange() {
      const opts = this.selectedOptions();
      opts.forEach((v, i) => { const l = $(`[data-option-label="${i}"]`, this); if (l) l.textContent = v; });
      const variant = this.variants.find(v => v.options.every((o, i) => o === opts[i]));
      this.update(variant);
    }
    update(variant) {
      const idInput = $('[data-variant-id]', this);
      const btn = $('[data-add-button]', this);
      const label = btn && $('[data-add-label]', btn);
      const stock = $('[data-availability]', this);
      const stockText = $('[data-availability-text]', this);
      const priceWrap = $('[data-price-wrap]', this);

      if (!variant) {
        if (btn) btn.disabled = true;
        if (label) label.textContent = strings.unavailable || 'Unavailable';
        if (stock) { stock.classList.add('is-out'); stockText.textContent = strings.unavailable || 'Unavailable'; }
        this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: null } }));
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (btn) btn.disabled = !variant.available;
      if (label) label.textContent = variant.available ? strings.addToCart : strings.soldOut;
      if (stock) {
        stock.classList.toggle('is-out', !variant.available);
        stock.classList.remove('is-low');
        stockText.textContent = variant.available ? strings.inStock : strings.soldOut;
      }
      const save = $('[data-save]', this);
      if (save) {
        const diff = (variant.compare_at_price || 0) - variant.price;
        save.hidden = diff <= 0;
        if (diff > 0) save.textContent = 'You save ' + formatMoney(diff);
      }
      if (priceWrap) {
        const sale = variant.compare_at_price && variant.compare_at_price > variant.price;
        priceWrap.innerHTML = `<div class="price price--lg${sale ? ' price--sale' : ''}" data-price><span class="price__current">${formatMoney(variant.price)}</span>${sale ? `<s class="price__compare"><span class="visually-hidden">Regular price</span>${formatMoney(variant.compare_at_price)}</s>` : ''}</div>`;
      }
      const page = this.closest('[data-product-page]');
      if (page) {
        const sku = $('[data-sku]', page);
        const skuRow = $('[data-spec-sku]', page);
        if (sku) sku.textContent = variant.sku || '';
        if (skuRow) skuRow.hidden = !variant.sku;
      }
      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }
  }
  customElements.define('product-buy', ProductBuy);

  /* ---------- product gallery + lightbox ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = $('[data-gallery-track]', this);
      this.slides = $$('.gallery__slide', this);
      this.thumbs = $$('[data-thumb]', this);
      this.lightbox = $('[data-lightbox]', this);
      const src = $('[data-lightbox-sources]', this);
      this.sources = src ? JSON.parse(src.textContent).filter(s => s.src) : [];
      this.lbIndex = 0;

      this.thumbs.forEach(t => t.addEventListener('click', () => this.go(parseInt(t.dataset.thumb, 10))));
      if (this.track) {
        this.track.addEventListener('scroll', debounce(() => this.syncFromScroll(), 60), { passive: true });
        this.track.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.current() + 1); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.current() - 1); }
        });
      }
      this.addEventListener('click', (e) => {
        const z = e.target.closest('[data-lightbox-open]');
        if (z) this.openLightbox(parseInt(z.dataset.lightboxOpen, 10));
        const step = e.target.closest('[data-lightbox-step]');
        if (step) this.stepLightbox(parseInt(step.dataset.lightboxStep, 10));
        if (e.target.closest('[data-lightbox-close]') || e.target === this.lightbox) this.lightbox.close();
      });
      if (this.lightbox) {
        this.lightbox.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') this.stepLightbox(1);
          if (e.key === 'ArrowLeft') this.stepLightbox(-1);
        });
        this.lightbox.addEventListener('close', () => { if (!openDrawers.length) document.documentElement.classList.remove('is-locked'); });
      }
      const page = this.closest('[data-product-page]') || document;
      page.addEventListener('variant:change', (e) => {
        const v = e.detail.variant;
        if (!v || !v.featured_media) return;
        const idx = this.slides.findIndex(s => s.dataset.mediaId === String(v.featured_media.id));
        if (idx > -1) this.go(idx);
      });
      const active = this.slides.findIndex(s => s.classList.contains('is-active'));
      if (active > 0) requestAnimationFrame(() => this.go(active, true));
    }
    current() { return Math.max(0, this.slides.findIndex(s => s.classList.contains('is-active'))); }
    go(i, instant) {
      if (!this.slides.length) return;
      i = (i + this.slides.length) % this.slides.length;
      const slide = this.slides[i];
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: instant || reduceMotion ? 'auto' : 'smooth' });
      this.setActive(i);
    }
    setActive(i) {
      this.slides.forEach((s, n) => s.classList.toggle('is-active', n === i));
      this.thumbs.forEach((t, n) => { t.classList.toggle('is-active', n === i); if (n === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
      const at = this.thumbs[i];
      if (at && at.parentElement.scrollWidth > at.parentElement.clientWidth) at.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    syncFromScroll() {
      const x = this.track.scrollLeft;
      let best = 0, dist = Infinity;
      this.slides.forEach((s, n) => { const d = Math.abs(s.offsetLeft - this.track.offsetLeft - x); if (d < dist) { dist = d; best = n; } });
      this.setActive(best);
    }
    openLightbox(mediaIndex) {
      if (!this.lightbox || !this.sources.length) return;
      const pos = this.sources.findIndex(s => s.i === mediaIndex);
      this.lbIndex = pos > -1 ? pos : 0;
      this.showLightbox();
      document.documentElement.classList.add('is-locked');
      this.lightbox.showModal();
    }
    stepLightbox(d) { this.lbIndex = (this.lbIndex + d + this.sources.length) % this.sources.length; this.showLightbox(); }
    showLightbox() {
      const img = $('[data-lightbox-img]', this.lightbox);
      const s = this.sources[this.lbIndex];
      img.src = s.src; img.alt = s.alt || '';
      $$('[data-lightbox-step]', this.lightbox).forEach(b => { b.hidden = this.sources.length < 2; });
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ---------- sticky add-to-cart bar ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const page = this.closest('[data-product-page]');
      const target = page && $('.product__info [data-add-button]', page);
      if (!target || !('IntersectionObserver' in window)) return;
      this.hidden = false;
      const io = new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', past);
        document.body.classList.toggle('has-satc', past);
      });
      io.observe(target);
      page.addEventListener('variant:change', (e) => {
        const v = e.detail.variant;
        const btn = $('[data-satc-button]', this);
        const price = $('[data-satc-price]', this);
        if (!v) { btn.disabled = true; btn.textContent = strings.unavailable; return; }
        btn.disabled = !v.available;
        btn.textContent = v.available ? strings.addToCart : strings.soldOut;
        if (price) price.textContent = formatMoney(v.price);
      });
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- sticky header ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      if (this.dataset.sticky !== 'true') { this.classList.add('is-static'); return; }
      const onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 8);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- mega menu ---------- */
  class DetailsHover extends HTMLElement {
    connectedCallback() {
      this.link = $('.nav__link', this);
      let t;
      const open = () => { clearTimeout(t); this.classList.add('is-open'); this.link.setAttribute('aria-expanded', 'true'); };
      const close = () => { t = setTimeout(() => { this.classList.remove('is-open'); this.link.setAttribute('aria-expanded', 'false'); }, 120); };
      this.addEventListener('mouseenter', open);
      this.addEventListener('mouseleave', close);
      this.addEventListener('focusin', open);
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.classList.remove('is-open'); this.link.setAttribute('aria-expanded', 'false'); this.link.focus(); } });
    }
  }
  customElements.define('details-hover', DetailsHover);

  /* ---------- announcement rotator ---------- */
  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      const msgs = $$('.announcement__msg', this);
      if (msgs.length < 2) return;
      let i = 0, paused = false;
      this.addEventListener('mouseenter', () => { paused = true; });
      this.addEventListener('mouseleave', () => { paused = false; });
      this.addEventListener('focusin', () => { paused = true; });
      this.addEventListener('focusout', () => { paused = false; });
      setInterval(() => {
        if (paused || document.hidden) return;
        msgs[i].classList.remove('is-active');
        i = (i + 1) % msgs.length;
        msgs[i].classList.add('is-active');
      }, parseInt(this.dataset.interval, 10) || 5000);
    }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  /* ---------- predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.panel = this.closest('.search-panel');
      this.input = $('input[type="search"]', this);
      this.results = $('[data-predictive-results]', this);
      this.clearBtn = $('[data-search-clear]', this);
      this.input.addEventListener('input', debounce(() => this.search(), 220));
      this.input.addEventListener('input', () => { this.clearBtn.hidden = !this.input.value; });
      this.clearBtn.addEventListener('click', () => { this.input.value = ''; this.clearBtn.hidden = true; this.results.innerHTML = ''; this.input.focus(); });
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-search-open]');
        if (opener) { e.preventDefault(); this.panel.hidden ? this.open() : this.close(); return; }
        if (!this.panel.hidden && e.target.closest('[data-search-close]')) this.close();
      });
      this.panel.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { this.close(); return; }
        if (e.key !== 'Tab') return;
        const f = $$('a[href], button:not([disabled]), input:not([type="hidden"])', this.panel).filter(el => el.offsetParent !== null);
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      });
      this.chips = $('[data-search-chips]', this);
      this.input.addEventListener('input', () => { if (this.chips) this.chips.hidden = !!this.input.value.trim(); });
    }
    open() {
      this.opener = document.activeElement;
      this.panel.hidden = false;
      document.documentElement.classList.add('is-locked');
      requestAnimationFrame(() => requestAnimationFrame(() => this.panel.classList.add('is-open')));
      $$('[data-search-open]').forEach(b => b.setAttribute('aria-expanded', 'true'));
      setTimeout(() => this.input.focus({ preventScroll: true }), 30);
    }
    close() {
      this.panel.classList.remove('is-open');
      this.panel.hidden = true;
      if (!openDrawers.length && !$('dialog[open]')) document.documentElement.classList.remove('is-locked');
      $$('[data-search-open]').forEach(b => { b.setAttribute('aria-expanded', 'false'); });
      const back = this.opener && document.contains(this.opener) && this.opener.offsetParent !== null ? this.opener : $('.header [data-search-open]');
      if (back) back.focus({ preventScroll: true });
    }
    async search() {
      const q = this.input.value.trim();
      if (this.ctrl) this.ctrl.abort();
      if (!q) { this.results.innerHTML = ''; return; }
      this.ctrl = new AbortController();
      this.results.classList.add('is-loading');
      try {
        const url = `${routes.predictiveSearch || '/search/suggest'}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=product,collection,query&resources[limit]=6&resources[options][unavailable_products]=last`;
        const res = await fetch(url, { signal: this.ctrl.signal });
        if (!res.ok) throw new Error();
        const html = await res.text();
        const section = parseSection(html, '#shopify-section-predictive-search');
        this.results.innerHTML = section ? section.innerHTML : '';
      } catch (err) {
        if (err.name !== 'AbortError') this.results.innerHTML = '<p class="predictive__empty">Search is unavailable right now. Press Enter to see full results.</p>';
      } finally {
        this.results.classList.remove('is-loading');
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- quick view ---------- */
  const qv = document.getElementById('QuickViewModal');
  let qvOpener = null;
  document.addEventListener('click', async (e) => {
    const trigger = e.target.closest('[data-quick-view]');
    if (!trigger || !qv) return;
    e.preventDefault();
    qvOpener = trigger;
    const body = $('[data-quick-view-body]', qv);
    body.innerHTML = '<div class="spinner" role="status" aria-label="Loading"></div>';
    document.documentElement.classList.add('is-locked');
    qv.showModal();
    try {
      const url = trigger.dataset.quickView;
      const res = await fetch(url + (url.includes('?') ? '&' : '?') + 'section_id=quick-view');
      if (!res.ok) throw new Error();
      const section = parseSection(await res.text(), '.qv');
      body.innerHTML = '';
      if (section) body.appendChild(section); else throw new Error();
    } catch (err) {
      body.innerHTML = `<div class="empty-state"><p class="h3">Couldn't load this product</p><a class="btn btn--primary" href="${trigger.dataset.quickView}">Open product page</a></div>`;
    }
  });
  if (qv) {
    qv.addEventListener('click', (e) => { if (e.target === qv || e.target.closest('[data-modal-close]')) qv.close(); });
    qv.addEventListener('close', () => {
      if (!openDrawers.length) document.documentElement.classList.remove('is-locked');
      if (qvOpener && document.contains(qvOpener) && !$('#CartDrawer.is-open')) qvOpener.focus();
    });
  }

  /* ---------- recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const fresh = parseSection(await res.text(), 'product-recommendations');
          if (fresh && fresh.innerHTML.trim()) this.innerHTML = fresh.innerHTML; else this.remove();
        } catch (e) { this.remove(); }
      };
      if (!('IntersectionObserver' in window)) return load();
      const io = new IntersectionObserver((entries) => { if (entries[0].isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- recently viewed ---------- */
  const RV_KEY = 'vault_recent';
  const readRecent = () => { try { return JSON.parse(localStorage.getItem(RV_KEY)) || []; } catch (e) { return []; } };
  const pageProduct = $('[data-product-page]');
  if (pageProduct) {
    const h = pageProduct.dataset.handle;
    try { localStorage.setItem(RV_KEY, JSON.stringify([h, ...readRecent().filter(x => x !== h)].slice(0, 12))); } catch (e) {}
  }

  class RecentlyViewed extends HTMLElement {
    async connectedCallback() {
      const current = pageProduct ? pageProduct.dataset.handle : null;
      const limit = parseInt(this.dataset.limit, 10) || 4;
      const handles = readRecent().filter(h => h !== current).slice(0, limit);
      if (!handles.length) return;
      const list = $('[data-recent-list]', this);
      const root = (routes.root || '/').replace(/\/$/, '');
      const products = (await Promise.all(handles.map(h => fetch(`${root}/products/${encodeURIComponent(h)}.js`).then(r => (r.ok ? r.json() : null)).catch(() => null)))).filter(Boolean);
      if (!products.length) return;
      products.forEach(p => {
        const li = document.createElement('li');
        const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=500` : '';
        const sale = p.compare_at_price && p.compare_at_price > p.price;
        li.innerHTML = `
          <article class="card">
            <div class="card__media">
              <a href="${p.url}" class="card__img-link" tabindex="-1" aria-hidden="true">${img ? `<img class="card__img" src="${img}" alt="" loading="lazy" width="500" height="500">` : '<div class="cover cover--guide"><span class="cover__grid"></span></div>'}</a>
              ${p.available ? '' : `<div class="card__badges"><span class="badge badge--muted">${strings.soldOut}</span></div>`}
            </div>
            <div class="card__info">
              <p class="card__meta"><span class="card__cat"></span></p>
              <h3 class="card__title"><a class="card__link" href="${p.url}"></a></h3>
              <div class="card__foot"><div class="price${sale ? ' price--sale' : ''}"><span class="price__current">${p.price_varies ? '<span class="price__from">From</span> ' : ''}${formatMoney(p.price)}</span>${sale ? `<s class="price__compare">${formatMoney(p.compare_at_price)}</s>` : ''}</div></div>
            </div>
          </article>`;
        $('.card__cat', li).textContent = p.type || 'Resource';
        $('.card__link', li).textContent = p.title;
        list.appendChild(li);
      });
      this.hidden = false;
    }
  }
  customElements.define('recently-viewed', RecentlyViewed);

  /* ---------- collection & search filters ---------- */
  class FacetResults extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.onChange = debounce(() => this.submit(), 450);
      this.addEventListener('change', (e) => { if (e.target.closest('[data-facets-form]') || e.target.matches('[data-sort]')) this.onChange(); });
      this.addEventListener('input', (e) => { if (e.target.matches('input[type="number"]')) this.onChange(); });
      this.addEventListener('submit', (e) => { if (e.target.matches('[data-facets-form]')) { e.preventDefault(); this.submit(); } });
      this.addEventListener('click', (e) => {
        const a = e.target.closest('a[data-facet-link]');
        if (!a) return;
        e.preventDefault();
        this.load(a.href, true);
        if (a.closest('.pagination')) this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      });
      window.addEventListener('popstate', () => this.load(window.location.href, false));
    }
    submit() {
      const form = $('[data-facets-form]', this);
      const params = new URLSearchParams();
      new FormData(form).forEach((v, k) => { if (v !== '') params.append(k, v); });
      const url = `${window.location.pathname}?${params.toString()}`;
      this.load(url, true);
    }
    async load(href, push) {
      const url = new URL(href, window.location.origin);
      const fetchUrl = new URL(url);
      fetchUrl.searchParams.set('section_id', this.sectionId);
      const filtersOpen = $('#FacetsDrawer.is-open', this) !== null;
      this.classList.add('is-loading');
      try {
        const res = await fetch(fetchUrl.toString());
        if (!res.ok) throw new Error();
        const fresh = parseSection(await res.text(), 'facet-results');
        if (!fresh) throw new Error();
        const focusedName = document.activeElement && this.contains(document.activeElement) ? document.activeElement.name : null;
        this.innerHTML = fresh.innerHTML;
        if (filtersOpen) openDrawer('FacetsDrawer', { instant: true, silent: true });
        if (focusedName) { const el = this.querySelector(`[name="${CSS.escape(focusedName)}"]`); el && el.focus({ preventScroll: true }); }
        if (push) window.history.pushState({}, '', url.toString());
        reveal(this);
      } catch (e) {
        window.location.href = url.toString();
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }
  customElements.define('facet-results', FacetResults);

  /* ---------- newsletter validation ---------- */
  document.addEventListener('submit', (e) => {
    const email = e.target.querySelector && e.target.querySelector('[data-validate-email]');
    if (!email) return;
    const err = document.getElementById(email.getAttribute('aria-describedby'));
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.value.trim());
    if (!ok) {
      e.preventDefault();
      email.setAttribute('aria-invalid', 'true');
      if (err) err.hidden = false;
      email.focus();
    } else {
      email.removeAttribute('aria-invalid');
      if (err) err.hidden = true;
      const btn = e.target.querySelector('[type="submit"]');
      if (btn) btn.classList.add('is-loading');
    }
  });

  /* ---------- reveal on scroll ---------- */
  function reveal(ctx = document) {
    const els = $$('[data-reveal]:not(.is-in)', ctx);
    const designMode = window.Shopify && window.Shopify.designMode;
    if (!P.settings || !P.settings.animations || reduceMotion || designMode || !('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('is-in'));
      return;
    }
    document.documentElement.classList.add('has-reveal');
    const io = new IntersectionObserver((entries) => {
      entries.forEach(en => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach(el => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight) el.classList.add('is-in'); else io.observe(el);
    });
  }
  reveal();

  /* ---------- split headings into words for staggered entrance ---------- */
  function splitWords(el) {
    if (el.dataset.splitDone) return;
    el.dataset.splitDone = '1';
    let i = 0;
    const walk = (node) => {
      Array.from(node.childNodes).forEach(child => {
        if (child.nodeType === 3) {
          const parts = child.textContent.split(/(\s+)/);
          const frag = document.createDocumentFragment();
          parts.forEach(part => {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
            const w = document.createElement('span');
            w.className = 'w';
            w.style.setProperty('--wi', i++);
            const inner = document.createElement('span');
            inner.textContent = part;
            w.appendChild(inner);
            frag.appendChild(w);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === 1) walk(child);
      });
    };
    walk(el);
  }
  $$('[data-split]').forEach(el => { splitWords(el); el.setAttribute('data-reveal', ''); });
  reveal();

  /* ---------- count up (real numbers only, e.g. product count) ---------- */
  function countUp(el) {
    const to = parseInt(el.dataset.countTo, 10);
    if (!to || reduceMotion) return;
    const start = performance.now(), dur = 1200;
    const tick = (t) => {
      const k = Math.min(1, (t - start) / dur);
      el.textContent = Math.round(to * (1 - Math.pow(1 - k, 3)));
      if (k < 1) requestAnimationFrame(tick);
    };
    el.textContent = '0';
    requestAnimationFrame(tick);
  }
  $$('[data-count-to]').forEach(countUp);

  /* ---------- pointer helpers (desktop only) ---------- */
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  if (finePointer && !reduceMotion && P.settings && P.settings.magnetic) {
    document.addEventListener('pointermove', (e) => {
      const btn = e.target.closest && e.target.closest('[data-magnetic]');
      if (!btn || btn.disabled) return;
      const r = btn.getBoundingClientRect();
      const x = (e.clientX - (r.left + r.width / 2)) / r.width;
      const y = (e.clientY - (r.top + r.height / 2)) / r.height;
      btn.style.setProperty('--mx', (x * 10).toFixed(2) + 'px');
      btn.style.setProperty('--my', (y * 8).toFixed(2) + 'px');
    }, { passive: true });
    document.addEventListener('pointerout', (e) => {
      const btn = e.target.closest && e.target.closest('[data-magnetic]');
      if (btn && !btn.contains(e.relatedTarget)) { btn.style.removeProperty('--mx'); btn.style.removeProperty('--my'); }
    });
  }

  if (finePointer && !reduceMotion) {
    // hero parallax
    $$('[data-parallax]').forEach(stage => {
      const layers = $$('[data-depth]', stage);
      const vault = $('.vault', stage);
      let raf = null;
      stage.closest('section').addEventListener('pointermove', (e) => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = null;
          const r = stage.getBoundingClientRect();
          const x = (e.clientX - (r.left + r.width / 2)) / r.width;
          const y = (e.clientY - (r.top + r.height / 2)) / r.height;
          layers.forEach(l => { const d = parseFloat(l.dataset.depth) || 10; l.style.setProperty('--px', (x * d).toFixed(1) + 'px'); l.style.setProperty('--py', (y * d).toFixed(1) + 'px'); });
          if (vault) { vault.style.setProperty('--rx', (y * -6).toFixed(2) + 'deg'); vault.style.setProperty('--ry', (x * 8).toFixed(2) + 'deg'); }
        });
      }, { passive: true });
    });
    // tilt cards
    document.addEventListener('pointermove', (e) => {
      const card = e.target.closest && e.target.closest('[data-tilt]');
      if (!card) return;
      const r = card.getBoundingClientRect();
      card.style.setProperty('--tx', ((e.clientX - r.left) / r.width * 100).toFixed(1) + '%');
      card.style.setProperty('--ty', ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%');
    }, { passive: true });
    // ambient glow follows pointer, softly
    if (document.body.classList.contains('has-ambient')) {
      let raf2 = null;
      window.addEventListener('pointermove', (e) => {
        if (raf2) return;
        raf2 = requestAnimationFrame(() => {
          raf2 = null;
          const g = document.querySelector('.ambient__glow');
          if (g) { g.style.setProperty('--gxp', e.clientX + 'px'); g.style.setProperty('--gyp', e.clientY + 'px'); }
        });
      }, { passive: true });
    }
  }

  /* ---------- pause looping animations off screen ---------- */
  if ('IntersectionObserver' in window) {
    const pauseIO = new IntersectionObserver((entries) => {
      entries.forEach(en => en.target.classList.toggle('is-offscreen', !en.isIntersecting));
    }, { rootMargin: '100px 0px' });
    $$('.hero, .final, .announcement, .footer').forEach(el => pauseIO.observe(el));
  }

  /* ---------- scroll indicator ---------- */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-scroll-next]');
    if (!a) return;
    const id = (a.getAttribute('href') || '').slice(1);
    let target = id && id !== 'MainContent' ? document.getElementById(id) : null;
    if (!target) { const host = a.closest('.shopify-section'); target = host && host.nextElementSibling; }
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  });

  /* ---------- product rail ---------- */
  class ProductRail extends HTMLElement {
    connectedCallback() {
      this.track = $('.rail__track', this);
      this.bar = $('[data-rail-progress]', this);
      const section = this.closest('section');
      this.prev = section && $('[data-rail-prev]', section);
      this.next = section && $('[data-rail-next]', section);
      const step = () => Math.max(260, this.track.clientWidth * 0.8);
      this.prev && this.prev.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
      this.next && this.next.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
      this.track.addEventListener('scroll', () => this.sync(), { passive: true });
      window.addEventListener('resize', debounce(() => this.sync(), 100));
      this.sync();
    }
    sync() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const k = max > 0 ? this.track.scrollLeft / max : 1;
      if (this.bar) { this.bar.style.setProperty('--k', k.toFixed(3)); this.bar.style.setProperty('--vis', Math.min(1, this.track.clientWidth / this.track.scrollWidth).toFixed(3)); }
      if (this.prev) this.prev.disabled = this.track.scrollLeft < 4;
      if (this.next) this.next.disabled = this.track.scrollLeft > max - 4;
      this.classList.toggle('is-static', max <= 4);
    }
  }
  customElements.define('product-rail', ProductRail);

  /* ---------- scroll-linked effects (timeline + mission words) ---------- */
  const scrubbers = [];
  class SystemTimeline extends HTMLElement {
    connectedCallback() {
      this.fill = $('[data-timeline-fill]', this);
      this.steps = $$('[data-step]', this);
      scrubbers.push(this);
      this.update();
    }
    update() {
      const r = this.getBoundingClientRect();
      const vh = window.innerHeight;
      let k = (vh * 0.75 - r.top) / (r.height + vh * 0.25);
      if (reduceMotion || !P.settings.animations) k = 1;
      k = Math.max(this.maxK || 0, Math.max(0, Math.min(1, k)));
      this.maxK = k;
      if (this.fill) this.fill.style.setProperty('--k', k.toFixed(3));
      const n = this.steps.length;
      this.steps.forEach((s, i) => s.classList.toggle('is-on', k >= (i + 0.35) / n || k === 1));
    }
  }
  customElements.define('system-timeline', SystemTimeline);

  $$('[data-scrub]').forEach(el => {
    splitWords(el);
    const words = $$('.w', el);
    scrubbers.push({
      update() {
        if (reduceMotion || !P.settings.animations) { words.forEach(w => w.classList.add('is-lit')); return; }
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight;
        const k = Math.max(0, Math.min(1, (vh * 0.85 - r.top) / (r.height + vh * 0.35)));
        const lit = Math.round(k * words.length * 1.15);
        words.forEach((w, i) => w.classList.toggle('is-lit', i < lit));
      }
    });
  });
  let scrubRaf = null;
  const runScrub = () => { scrubRaf = null; scrubbers.forEach(s => s.update()); };
  window.addEventListener('scroll', () => { if (!scrubRaf) scrubRaf = requestAnimationFrame(runScrub); }, { passive: true });
  window.addEventListener('resize', debounce(runScrub, 100));
  runScrub();

  /* ---------- tabs (resource preview) ---------- */
  class TabSwitcher extends HTMLElement {
    connectedCallback() {
      this.tabs = $$('[role="tab"]', this);
      this.tabs.forEach((t, i) => {
        t.addEventListener('click', () => this.select(i));
        t.addEventListener('keydown', (e) => {
          const vertical = window.matchMedia('(min-width: 990px)').matches;
          const nextKey = vertical ? 'ArrowDown' : 'ArrowRight';
          const prevKey = vertical ? 'ArrowUp' : 'ArrowLeft';
          if (e.key === nextKey || e.key === prevKey) {
            e.preventDefault();
            const j = (i + (e.key === nextKey ? 1 : -1) + this.tabs.length) % this.tabs.length;
            this.select(j); this.tabs[j].focus();
          }
        });
      });
    }
    select(i) {
      this.tabs.forEach((t, n) => {
        const on = n === i;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) { panel.hidden = !on; panel.classList.toggle('is-active', on); }
      });
    }
  }
  customElements.define('tab-switcher', TabSwitcher);
  document.addEventListener('shopify:block:select', (e) => {
    const ts = e.target.closest && e.target.closest('tab-switcher');
    if (ts && e.target.getAttribute('role') === 'tab') ts.select(ts.tabs.indexOf(e.target));
  });

  /* ---------- bundle picker (bundle builder + frequently bought together) ---------- */
  class BundlePicker extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.boxes = $$('input[type="checkbox"]', this);
      this.btn = $('[data-pick-add]', this);
      this.total = $('[data-pick-total]', this);
      this.count = $('[data-pick-count]', this);
      this.list = $('[data-pick-list]', this);
      this.err = $('[data-pick-error]', this);
      this.addEventListener('change', () => this.sync());
      this.btn && this.btn.addEventListener('click', () => this.add());
      this.sync();
    }
    chosen() { return this.boxes.filter(b => b.checked && !b.disabled); }
    sync() {
      const c = this.chosen();
      const sum = c.reduce((a, b) => a + parseInt(b.dataset.price, 10), 0);
      if (this.total) this.total.textContent = formatMoney(sum);
      if (this.count) this.count.textContent = c.length;
      if (this.btn) this.btn.disabled = c.length === 0;
      if (this.list) {
        this.list.innerHTML = '';
        c.forEach(b => { const li = document.createElement('li'); li.textContent = b.dataset.title; this.list.appendChild(li); });
      }
      this.boxes.forEach(b => b.closest('.pick') && b.closest('.pick').classList.toggle('is-on', b.checked));
    }
    async add() {
      const c = this.chosen();
      if (!c.length) return;
      const label = $('[data-add-label]', this.btn);
      const original = label.textContent;
      this.btn.disabled = true; this.btn.classList.add('is-loading');
      if (this.err) this.err.hidden = true;
      try {
        const res = await fetch((routes.cartAdd || '/cart/add') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: c.map(b => ({ id: parseInt(b.value, 10), quantity: 1 })), sections: ['cart-drawer'], sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || strings.addError);
        Cart.render(data.sections);
        if (P.settings.cartDrawer && document.getElementById('CartDrawer')) openDrawer('CartDrawer');
        toast(c.length + (c.length === 1 ? ' item' : ' items') + ' added to your cart');
      } catch (e) {
        if (this.err) { this.err.textContent = e.message; this.err.hidden = false; } else toast(e.message, 'err');
      } finally {
        this.btn.classList.remove('is-loading'); this.btn.disabled = false; label.textContent = original;
      }
    }
  }
  customElements.define('bundle-picker', BundlePicker);

  /* ---------- theme editor support ---------- */
  document.addEventListener('shopify:section:load', (e) => {
    $$('[data-split]', e.target).forEach(el => { splitWords(el); el.setAttribute('data-reveal', ''); });
    $$('[data-count-to]', e.target).forEach(countUp);
    reveal(e.target);
    runScrub();
  });
  document.addEventListener('shopify:block:select', (e) => {
    const d = e.target.closest('details');
    if (d) d.open = true;
  });

  P.formatMoney = formatMoney;
  P.openDrawer = openDrawer;
  P.toast = toast;
  P.Cart = Cart;
})();
